import { LeftCircleOutlined, RightCircleOutlined } from "@ant-design/icons";
import { notification } from "antd";
import { MEMOIZED_DOMAIN_URL } from "components/util/HostUtils";
import { fabric } from "fabric";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Measure from "react-measure";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedRailInspectionImage } from "redux/actions";
import Ruler, { UNSELECTABLE_PROPERTIES } from "./fabric/ruler";

const BASE_URL = `https://inspection${MEMOIZED_DOMAIN_URL}/`;

const widgetRailImageLatPosSelector = (state) => _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "railInspectionLatPos"], 0);
const railImageLatPosSelector = (state) => state.railInspection.selectedRailInspectionImage.latPos;
const adjustmentsSelector = (state) => state.railInspection.detailImageAdjustments;
const routeIdSelector = (state) => state.playlist.data.routeID;
const csrfTokenSelector = (state) => state.csrfToken;
const widgetSelectedRailImageIndexSelector = (state) =>
    _.get(state.widgetData.DASHBOARD, [state.dashboardWidgetKey, "state", "position", "railInspectionIndex"], 0);
const selectedRailImageIndexSelector = (state) => state.railInspection.selectedRailInspectionImage.index;
const railDataObjectSelector = (state) => state.railInspection.railInspectionImages.data;
const railImagesSelector = (state) => _.get(state.schemaInterface.sessions, [state.schemaInterface.selectedSession], []);

const DIVISIONS_PER_METER = 10;

const RailImageDisplay = ({
    imageConfigItem,
    zoom,
    setZoom,
    syncOffset,
    isPatrol,
    sessionIsBackward,
    triggerSnapshot,
    setTriggerSnapshot,
    isWidget,
    widgetPosition,
    showGrid,
    isWidgetLayout,
    measureActive,
    setMeasureActive,
    handleClearRulersRef,
    setHasRulers,
}) => {
    const [dimensions, setDimensions] = useState({});
    const [fabricCanvas, setFabricCanvas] = useState(null);
    const [fabricCanvasRef, setFabricCanvasRef] = useState(null);
    const [canTakeSnapshot, setCanTakeSnapshot] = useState(false);
    const [imageAdjustments, setImageAdjustments] = useState({});

    const uncompleteRulerRef = useRef(null);

    const railImageLatPos = useSelector(widgetPosition ? widgetRailImageLatPosSelector : railImageLatPosSelector);
    const selectedRailImageIndexOriginal = useSelector(widgetPosition ? widgetSelectedRailImageIndexSelector : selectedRailImageIndexSelector);
    const currentAdjustments = useSelector(adjustmentsSelector);
    const routeID = useSelector(routeIdSelector);
    const csrfToken = useSelector(csrfTokenSelector);

    const imageRenderIdRef = useRef(0);
    const imageRefs = useRef(new Map());
    const arrowRef = useRef(null);
    const imageAdjustmentsRef = useRef([]);

    const dispatch = useDispatch();
    const rulers = useRef([]);

    const scaleFactorRef = useRef(1);

    const selectedRailImageIndex = selectedRailImageIndexOriginal + (syncOffset ?? 0);

    const inspectionImages = useSelector(railDataObjectSelector);
    const patrolImages = useSelector(railImagesSelector);

    const railImages = isPatrol ? patrolImages : inspectionImages;

    const imageScrollOffset = Math.round((railImageLatPos % 1) * 1000) / 1000;

    const currentImageAdjustments = useMemo(() => {
        return _.get(currentAdjustments, [_.get(imageConfigItem, "name", null)], {});
    }, [currentAdjustments, imageConfigItem]);

    const adjustmentsToFilters = useCallback((adjustments) => {
        const imageFilters = [];
        if (!_.isNil(adjustments.brightness)) {
            imageFilters.push(new fabric.Image.filters.Brightness({ brightness: adjustments.brightness }));
        }
        if (!_.isNil(adjustments.contrast)) {
            imageFilters.push(new fabric.Image.filters.Contrast({ contrast: adjustments.contrast }));
        }
        return imageFilters;
    }, []);

    useEffect(() => {
        if (!_.isEqual(imageAdjustments, currentImageAdjustments)) {
            imageAdjustmentsRef.current = adjustmentsToFilters(currentImageAdjustments);
            setImageAdjustments(currentImageAdjustments);
        }
    }, [imageAdjustments, currentImageAdjustments, adjustmentsToFilters, currentAdjustments]);

    const getRailImage = useCallback(
        (index, leftOffset, csrfToken) => {
            if (index < 0 || index >= railImages.length) return;

            const image = railImages[index];

            if (!image) return null;

            const { base_path: basePath, timestamp, position: imagePosition } = image;

            const newBasePath = basePath.replace("$DEVICE", `${imageConfigItem.source}`);
            const imageSource = `${BASE_URL}${newBasePath}/${image.timestamp}.jpg` + (csrfToken ? `?csrf=${csrfToken}` : "");

            return {
                leftOffset,
                index,
                imageSource,
                timestamp,
                imagePosition,
            };
        },
        [imageConfigItem, railImages],
    );

    const railImagesToDisplay = useMemo(() => {
        if (_.isEmpty(imageConfigItem)) return [];

        const imageWidth = imageConfigItem.display_width;
        const sideCount = Math.ceil(dimensions.width / imageWidth / 2);
        const offsets = Array.from({ length: 2 * sideCount + 1 }, (_, i) => i - sideCount);
        const leftOffset = imageScrollOffset + (imageScrollOffset < 0 ? 0.5 : -0.5);

        return offsets
            .map((offset) => {
                const index = selectedRailImageIndex + offset;
                const image = getRailImage(index, leftOffset, csrfToken);
                return image ? { ...image, offset, source: imageConfigItem.source } : null;
            })
            .filter(Boolean);
        // csrfToken excluded to avoid images being refreshed every minute when idle
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dimensions.width, getRailImage, imageConfigItem, imageScrollOffset, selectedRailImageIndex]);

    useEffect(() => {
        if (!fabricCanvasRef) return;

        const canvas = new fabric.Canvas(fabricCanvasRef, {
            defaultCursor: "move",
            hoverCursor: "move",
            selection: false, // true for debug
        });
        setFabricCanvas(canvas);

        return () => {
            canvas.clear();
            canvas.dispose();

            setFabricCanvas(null);
        };
    }, [fabricCanvasRef]);

    useEffect(() => {
        if (fabricCanvas) {
            fabricCanvas.setWidth(dimensions.width);
            fabricCanvas.setHeight(dimensions.height);
        }
    }, [dimensions, fabricCanvas]);

    useEffect(() => {
        if (uncompleteRulerRef.current) {
            uncompleteRulerRef.current.cleanup();
            rulers.current = rulers.current.filter((ruler) => ruler !== uncompleteRulerRef.current);
            setHasRulers(rulers.current.length > 0);
        }

        if (!measureActive || !fabricCanvas || imageRefs.current.size === 0) return;

        //selectedRailImageIndex might not be the one that they're drawing on
        //may need to account for scale factor when considering the position of the nodes too, hopefully not
        const centerImage = imageRefs.current.get(selectedRailImageIndex)?.img;
        if (!centerImage) return;

        const imageHeight = centerImage.height * centerImage.scaleX;
        const ruler = new Ruler(fabricCanvas, scaleFactorRef, setMeasureActive, uncompleteRulerRef, imageHeight);

        uncompleteRulerRef.current = ruler;
        rulers.current = [...rulers.current, ruler];
        setHasRulers(true);
    }, [fabricCanvas, measureActive, selectedRailImageIndex, setHasRulers, setMeasureActive]);

    const canvasRender = useCallback(() => {
        if (!fabricCanvas) return;
        if (fabricCanvas.contextContainer) {
            fabricCanvas.renderAll();
        }
    }, [fabricCanvas]);

    useEffect(() => {
        handleClearRulersRef.current = () => {
            rulers.current.forEach((ruler) => {
                ruler.cleanup();
            });
            rulers.current = [];
            setHasRulers(false);
        };
    }, [fabricCanvas, handleClearRulersRef, rulers, setHasRulers]);

    useEffect(() => {
        if (!handleClearRulersRef.current) return;

        handleClearRulersRef.current();
        canvasRender();
    }, [canvasRender, handleClearRulersRef, imageScrollOffset, selectedRailImageIndex]);

    const drawArrow = useCallback(
        (arrowVerticalPos, arrowWidth, posLeft) => {
            if (arrowRef.current) {
                fabricCanvas.remove(arrowRef.current);
            }
            let arrowRight = imageConfigItem.rotate === "right";
            if (sessionIsBackward) {
                arrowRight = imageConfigItem.rotate === "left";
            }

            let objGroup;
            if (arrowRight) {
                const rightArrow = [
                    new fabric.Triangle({
                        ...UNSELECTABLE_PROPERTIES,
                        width: 28,
                        height: 22,
                        fill: "red",
                        left: posLeft + 5,
                        top: arrowVerticalPos - 14,
                        angle: 90,
                    }),
                    new fabric.Line([posLeft, arrowVerticalPos, posLeft - arrowWidth, arrowVerticalPos], {
                        ...UNSELECTABLE_PROPERTIES,
                        stroke: "red",
                        strokeWidth: 3,
                    }),
                ];
                objGroup = new fabric.Group(rightArrow, UNSELECTABLE_PROPERTIES);
            } else {
                const leftArrow = [
                    new fabric.Triangle({
                        ...UNSELECTABLE_PROPERTIES,
                        width: 28,
                        height: 22,
                        fill: "red",
                        left: Math.max(posLeft - 10, 0),
                        top: arrowVerticalPos + 14,
                        angle: -90,
                    }),
                    new fabric.Line([Math.max(posLeft, 5), arrowVerticalPos, posLeft + imageConfigItem.display_width, arrowVerticalPos], {
                        ...UNSELECTABLE_PROPERTIES,
                        stroke: "red",
                        strokeWidth: 3,
                    }),
                ];
                objGroup = new fabric.Group(leftArrow, UNSELECTABLE_PROPERTIES);
            }
            if (fabricCanvas.contextContainer) {
                fabricCanvas.add(objGroup);
            }

            arrowRef.current = objGroup;
        },
        [fabricCanvas, imageConfigItem.display_width, imageConfigItem.rotate, sessionIsBackward],
    );

    const calculateImagePositionProperties = useCallback(
        (img, imageData) => {
            // When imageConfigItem.rotate === left, the left value is actually the right side of the shape

            const SINGLE_IMAGE_WIDTH = imageConfigItem.display_width;
            const SQUASH_FACTOR = imageConfigItem.display_height / img.width;
            const widthRatioChange = SINGLE_IMAGE_WIDTH / img.height;
            const newWidth = img.height * SQUASH_FACTOR;

            const rotateLeft = imageConfigItem.rotate === "left";
            const isForwardOrientation = rotateLeft !== sessionIsBackward;
            const rotation = isForwardOrientation ? -90 : 90;
            const top = isForwardOrientation ? newWidth : 0;
            let left = isForwardOrientation ? 0 : SINGLE_IMAGE_WIDTH;

            const newImageWidth = img.height * widthRatioChange;

            scaleFactorRef.current = 1000 / newImageWidth;

            left += (dimensions.width - newImageWidth) / 2;

            const direction = rotateLeft ? -1 : 1;

            // Invert offsets if the footage was captured while the vehicle was moving backward
            if (imageData.leftOffset) left += imageData.leftOffset * newImageWidth * direction * (sessionIsBackward ? 1 : -1);
            if (imageData.offset !== undefined) left += imageData.offset * newImageWidth * direction * (sessionIsBackward ? -1 : 1);

            return {
                top,
                left: Math.floor(left),
                angle: rotation,
            };
        },
        [imageConfigItem.display_height, imageConfigItem.display_width, imageConfigItem.rotate, sessionIsBackward, dimensions.width],
    );

    const imageFilters = useMemo(() => {
        return adjustmentsToFilters(imageAdjustments);
    }, [adjustmentsToFilters, imageAdjustments]);

    useEffect(() => {
        if (!fabricCanvas) return;

        imageRefs.current.forEach(({ img, grid }) => {
            if (img) {
                img.filters = imageFilters;
                img.applyFilters();
            }

            if (grid) {
                grid.visible = showGrid;
            }
        });

        canvasRender();
    }, [canvasRender, fabricCanvas, imageFilters, showGrid]);

    const loadGrid = useCallback(
        (imagePositionProperties) => {
            const width = imageConfigItem.display_width;
            const height = imageConfigItem.display_height;
            const gridSize = width / DIVISIONS_PER_METER;

            let left = imagePositionProperties.left;
            if (imagePositionProperties.angle === 90) {
                left -= width;
            }

            const grid = [];
            const properties = {
                ...UNSELECTABLE_PROPERTIES,
                stroke: "#FF0000",
                strokeWidth: 1,
            };

            for (let i = 0; i <= height; i += gridSize) {
                grid.push(new fabric.Line([left, i, left + width, i], properties));
            }

            for (let i = 0; i <= width; i += gridSize) {
                grid.push(new fabric.Line([left + i, 0, left + i, height], properties));
            }

            return new fabric.Group(grid, UNSELECTABLE_PROPERTIES);
        },
        [imageConfigItem],
    );

    const loadFabricImage = useCallback(
        (SINGLE_IMAGE_WIDTH, img, imageData, ignoreFlip = false) => {
            const SQUASH_FACTOR = imageConfigItem.display_height / img.width;
            const widthRatioChange = SINGLE_IMAGE_WIDTH / img.height;
            const imagePositionProperties = calculateImagePositionProperties(img, imageData);

            // Draw arrow only for center image which is offset 0
            if (imageData.offset === 0) {
                const arrowPosLeft = imagePositionProperties.left;
                const verticalArrowPos = img.height * SQUASH_FACTOR + (dimensions.height - img.height * SQUASH_FACTOR) / 2;
                drawArrow(verticalArrowPos, SINGLE_IMAGE_WIDTH, arrowPosLeft);
            }

            const imageProperties = {
                ...UNSELECTABLE_PROPERTIES,
                scaleY: widthRatioChange,
                scaleX: SQUASH_FACTOR,
                railIndex: imageData.index,
                ...imagePositionProperties,
                source: imageConfigItem.source,
                flipX: ignoreFlip ? false : sessionIsBackward,
                railPosition: imageData.imagePosition,
                railTimestamp: imageData.timestamp,
            };

            const imgObj = img.set(imageProperties);
            const gridObj = loadGrid(imagePositionProperties);

            imgObj.imageIndex = imageData.index;
            imageRefs.current.set(imageData.index, { img: imgObj, grid: gridObj });

            return { imgObj, gridObj };
        },
        [calculateImagePositionProperties, dimensions.height, drawArrow, imageConfigItem.display_height, imageConfigItem.source, loadGrid, sessionIsBackward],
    );

    // rerender new images
    useEffect(() => {
        if (!fabricCanvas || !railImagesToDisplay.length) return;

        const SINGLE_IMAGE_WIDTH = imageConfigItem.display_width;

        const visibleOffsets = new Map(railImagesToDisplay.map((img) => [img.index, img.source]));
        for (const [offset, ref] of imageRefs.current) {
            if (!visibleOffsets.has(offset) || ref.img.source !== visibleOffsets.get(offset)) {
                if (ref.img) {
                    fabricCanvas.remove(ref.img);
                    if (ref.img.dispose) ref.img.dispose();
                }
                if (ref.grid) {
                    fabricCanvas.remove(ref.grid);
                    if (ref.grid.dispose) ref.grid.dispose();
                }
                imageRefs.current.delete(offset);
            }
        }

        imageRenderIdRef.current += 1;
        const renderId = imageRenderIdRef.current;

        railImagesToDisplay.forEach((imageData) => {
            const existingRef = imageRefs.current.get(imageData.index);

            if (existingRef) {
                // Reuse the existing Fabric objects
                const { img, grid } = existingRef;
                const newPosition = calculateImagePositionProperties(img, imageData);

                img.set({ ...newPosition });
                img.setCoords();

                grid.set({ left: newPosition.left - (newPosition.angle === 90 ? SINGLE_IMAGE_WIDTH : 0) });
                grid.setCoords();

                if (imageData.offset === 0) {
                    const SQUASH_FACTOR = imageConfigItem.display_height / img.width;
                    const verticalArrowPos = img.height * SQUASH_FACTOR + (dimensions.height - img.height * SQUASH_FACTOR) / 2;
                    const arrowPosLeft = newPosition.left;
                    drawArrow(verticalArrowPos, SINGLE_IMAGE_WIDTH, arrowPosLeft);
                }
            } else {
                fabric.Image.fromURL(
                    imageData.imageSource,
                    function (img, isError) {
                        if (renderId !== imageRenderIdRef.current) return;
                        if (isError) {
                            if (imageData.offset === 0) {
                                setCanTakeSnapshot(false);
                            }
                            // if image not loaded, load fallback image instead
                            const fallbackImgUrl = sessionIsBackward
                                ? imageConfigItem.rotate === "right"
                                    ? "/image_not_available_rotated_left.png"
                                    : "/image_not_available_rotated_right.png"
                                : imageConfigItem.rotate === "left"
                                  ? "/image_not_available_rotated_left.png"
                                  : "/image_not_available_rotated_right.png";

                            fabric.Image.fromURL(
                                fallbackImgUrl,
                                function (newImg) {
                                    const { imgObj, gridObj } = loadFabricImage(SINGLE_IMAGE_WIDTH, newImg, imageData, true);
                                    fabricCanvas.add(imgObj);
                                    gridObj.visible = showGrid;
                                    fabricCanvas.add(gridObj);
                                },
                                { crossOrigin: "anonymous" },
                            );

                            return;
                        }

                        if (imageData.offset === 0) {
                            setCanTakeSnapshot(true);
                        }

                        const { imgObj, gridObj } = loadFabricImage(SINGLE_IMAGE_WIDTH, img, imageData);
                        if (fabricCanvas.contextContainer) {
                            gridObj.visible = showGrid;
                            imgObj.filters = imageAdjustmentsRef.current;
                            imgObj.applyFilters();
                            fabricCanvas.add(imgObj);
                            fabricCanvas.add(gridObj);
                        }
                    },
                    { crossOrigin: "anonymous" },
                );
            }
        });
    }, [
        calculateImagePositionProperties,
        dimensions.height,
        drawArrow,
        fabricCanvas,
        imageConfigItem,
        loadFabricImage,
        railImagesToDisplay,
        sessionIsBackward,
        showGrid,
    ]);

    const limitCanvasPosition = useCallback((canvas) => {
        const canvasViewPort = canvas.viewportTransform;

        const bottomEndPoint = canvas.height * (canvasViewPort[0] - 1);
        if (canvasViewPort[5] >= 0 || -bottomEndPoint > canvasViewPort[5]) {
            canvasViewPort[5] = canvasViewPort[5] >= 0 ? 0 : -bottomEndPoint;
        }

        const rightEndPoint = canvas.width * (canvasViewPort[0] - 1);
        if (canvasViewPort[4] >= 0 || -rightEndPoint > canvasViewPort[4]) {
            canvasViewPort[4] = canvasViewPort[4] >= 0 ? 0 : -rightEndPoint;
        }
        canvas.viewportTransform = canvasViewPort;
    }, []);

    const onMove = useCallback(
        (opt, canvas) => {
            if (canvas.isDragging) {
                const delta = new fabric.Point(opt.e.movementX, opt.e.movementY);
                canvas.relativePan(delta);
                limitCanvasPosition(canvas);
            }
        },
        [limitCanvasPosition],
    );

    const onScroll = useCallback(
        (opt) => {
            let canvasZoom = fabricCanvas.getZoom();
            if (opt) {
                const delta = opt.e.deltaY;
                canvasZoom *= 0.999 ** delta;
                canvasZoom = Math.max(Math.min(7, canvasZoom), 1);
                fabricCanvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, canvasZoom);
                opt.e.preventDefault();
                opt.e.stopPropagation();
                setZoom(canvasZoom);
            }
            limitCanvasPosition(fabricCanvas);
        },
        [fabricCanvas, limitCanvasPosition, setZoom],
    );

    const handleMove = useCallback(
        (opt) => {
            onMove(opt, fabricCanvas);
        },
        [fabricCanvas, onMove],
    );

    const startDrag = useCallback(() => {
        fabricCanvas.isDragging = true;
    }, [fabricCanvas]);

    const stopDrag = useCallback(() => {
        fabricCanvas.setViewportTransform(fabricCanvas.viewportTransform);
        fabricCanvas.isDragging = false;
    }, [fabricCanvas]);

    useEffect(() => {
        if (fabricCanvas) {
            fabricCanvas.on("mouse:down", startDrag);
            fabricCanvas.on("mouse:move", handleMove);
            fabricCanvas.on("mouse:up", stopDrag);
            fabricCanvas.on("mouse:wheel", onScroll);

            return () => {
                fabricCanvas.off("mouse:down", startDrag);
                fabricCanvas.off("mouse:move", handleMove);
                fabricCanvas.off("mouse:up", stopDrag);
                fabricCanvas.off("mouse:wheel", onScroll);
            };
        }
    }, [fabricCanvas, handleMove, onScroll, startDrag, stopDrag]);

    useEffect(() => {
        if (!fabricCanvas || fabricCanvas.getZoom === zoom) return;

        let x = fabricCanvas.width / 2;
        let y = fabricCanvas.height / 2;
        fabricCanvas.zoomToPoint(new fabric.Point(x, y), zoom);
        limitCanvasPosition(fabricCanvas);
        canvasRender();
    }, [fabricCanvas, zoom, limitCanvasPosition, canvasRender]);

    const renderSnapshotCanvas = useCallback(() => {
        const SINGLE_IMAGE_WIDTH = imageConfigItem.display_width;

        // if below set to 1 the generated image will be the same size in pixels as the canvas in detailed view
        // otherwise we are scaling canvas and images up so it will get highest resolution
        const SCALE_FACTOR = imageConfigItem.img_width / imageConfigItem.display_height;

        // Create a new off-screen Fabric canvas
        const canvas = new fabric.StaticCanvas(null, {
            width: dimensions.width * SCALE_FACTOR,
            isDrawingMode: false,
            backgroundColor: "black",
        });

        let canvasHeightSet = false;

        const imagePromises = railImagesToDisplay.map((imageData) => {
            return new Promise((resolve, reject) => {
                fabric.Image.fromURL(
                    imageData.imageSource,
                    (img) => {
                        if (!canvasHeightSet) {
                            canvas.setHeight(img.height);
                            canvasHeightSet = true;
                        }
                        const SQUASH_FACTOR = imageConfigItem.display_height / img.width;
                        const widthRatioChange = SINGLE_IMAGE_WIDTH / img.height;
                        let imagePositionProperties = calculateImagePositionProperties(img, imageData);
                        imagePositionProperties["left"] = imagePositionProperties.left * SCALE_FACTOR;
                        imagePositionProperties["top"] = imagePositionProperties.top * SCALE_FACTOR;

                        const imageProperties = {
                            ...UNSELECTABLE_PROPERTIES,
                            scaleY: widthRatioChange * SCALE_FACTOR,
                            scaleX: SQUASH_FACTOR * SCALE_FACTOR,
                            railIndex: imageData.index,
                            ...imagePositionProperties,
                            flipX: sessionIsBackward,
                        };

                        const imgObj = img.set(imageProperties);

                        // apply filters to image object
                        imgObj.filters = imageFilters;
                        imgObj.applyFilters();

                        canvas.add(imgObj);
                        resolve();
                    },
                    { crossOrigin: "anonymous" },
                );
            });
        });

        // Convert the off-screen canvas to a data URL when all images loaded.
        Promise.all(imagePromises)
            .then(() => {
                const dataURL = canvas.toDataURL({ format: "png", quality: 1 });

                const now = moment();
                const formattedDateTime = now.format("DD-MM-YYYY-HH-mm");
                const fileName = `Snapshot_from_#${routeID}_${_.get(imageConfigItem, "name", "Detail_rail").replace(" ", "_")}_Rail_${formattedDateTime}.png`;

                // Create a temporary anchor element to trigger the download
                const link = document.createElement("a");
                link.href = dataURL;
                link.download = fileName;
                link.click();

                // Dispose of the off-screen canvas
                canvas.dispose();
            })
            .catch((error) => {
                notification.error({
                    message: "Error downloading image",
                    description: "There was an error downloading Detailed Images, try again.",
                });
                console.error("Error loading images:", error);
            });
    }, [calculateImagePositionProperties, dimensions.width, imageConfigItem, imageFilters, railImagesToDisplay, routeID, sessionIsBackward]);

    // useEffect to be triggered from parrent component RailInspectionExtraDisplay
    useEffect(() => {
        if (!triggerSnapshot) return;
        setTriggerSnapshot(false);

        if (!canTakeSnapshot) {
            return notification.info({
                message: "No image available",
            });
        }

        renderSnapshotCanvas();
    }, [canTakeSnapshot, renderSnapshotCanvas, setTriggerSnapshot, triggerSnapshot]);

    const navigate = useCallback(
        (right = true) => {
            let newIndex = selectedRailImageIndex;

            let rightForward = imageConfigItem.rotate === "right";
            if (sessionIsBackward) {
                rightForward = imageConfigItem.rotate === "left";
            }

            if ((rightForward && right) || (!rightForward && !right)) {
                newIndex += 1;
            } else {
                newIndex -= 1;
            }

            newIndex = Math.min(Math.max(0, newIndex), railImages.length - 1);
            const newImage = railImages[newIndex];

            dispatch(setSelectedRailInspectionImage(newIndex, newImage, false, 0.5));
        },
        [dispatch, imageConfigItem.rotate, railImages, selectedRailImageIndex, sessionIsBackward],
    );

    useEffect(() => {
        if (!fabricCanvas) return;

        if (!measureActive) {
            fabricCanvas.defaultCursor = "move";
            fabricCanvas.hoverCursor = "move";
            return;
        }

        fabricCanvas.defaultCursor = "none";
        fabricCanvas.hoverCursor = "none";
    }, [fabricCanvas, measureActive]);

    return (
        <Measure
            bounds
            onResize={(contentRect) => {
                setDimensions(contentRect.bounds);
            }}>
            {({ measureRef }) => {
                return (
                    <div
                        className={`InspectionScrollingCanvas${isWidget ? " is-widget" : ""}`}
                        ref={measureRef}
                        onContextMenu={(e) => e.preventDefault()}>
                        {isWidgetLayout && (
                            <div
                                className="DetailNavigationContainer Start"
                                style={{ height: imageConfigItem?.display_height }}>
                                <LeftCircleOutlined
                                    className="NavIcon"
                                    onClick={() => navigate(false)}
                                />
                            </div>
                        )}

                        <canvas
                            ref={(c) => setFabricCanvasRef(c)}
                            className="ScrollingFabricCanvas"
                        />

                        {isWidgetLayout && (
                            <div
                                className="DetailNavigationContainer End"
                                style={{ height: imageConfigItem?.display_height }}>
                                <RightCircleOutlined
                                    className="NavIcon"
                                    onClick={() => navigate(true)}
                                />
                            </div>
                        )}
                    </div>
                );
            }}
        </Measure>
    );
};

export default RailImageDisplay;
